import { Show, useGetList, useUpdate, useNotify, useRefresh, useNavigate } from 'react-admin';
import { Box, Typography, Paper, Grid, Avatar, Chip, Divider, Alert, Button, IconButton, CircularProgress, Tabs, Tab } from '@mui/material';
import type { ChipColor } from '@/types/chip';
import { Person, Work, School, TrendingUp, CheckCircle, Schedule, Warning, Email, Phone, LocationOn, Badge, CalendarMonth, Gavel, HealthAndSafety, Close, PlaylistAddCheck } from '@mui/icons-material';
import { PageDescription } from '@/components/help';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { EmployeeRecord, FileRecord, ONBOARDING_STAGES, STATUS_COLORS, STAGE_COLORS } from './constants';
import { EmployeeTraining } from './EmployeeTraining';
import { EmployeeDisciplinary } from './EmployeeDisciplinary';
import { EmployeeDocuments } from './EmployeeDocuments';
import { EmployeePIPs } from './EmployeePIPs';
import { EmployeeOHS } from './EmployeeOHS';
import { OnboardingProgress } from './OnboardingProgress';

export const EmployeeShow = () => {
  const { data: trainingData } = useGetList('training-records');
  const { data: disciplinaryData } = useGetList('disciplinary-cases');
  const { data: ohsData } = useGetList('workplace-injuries');
  const { data: pipData } = useGetList('pips');
  const { data: onboardingData } = useGetList('onboarding-processes');
  const { data: filesData } = useGetList('files');
  const [now] = useState(() => Date.now());
  const [activeTab, setActiveTab] = useState(0);
  const [update, { isLoading: isUpdating }] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();
  const navigate = useNavigate();

  return (
    <Show render={({ record }) => {
      if (!record) return null;
      const currentStageIdx = ONBOARDING_STAGES.indexOf(record.currentStage);
      const onboardingProgress = Math.round(((currentStageIdx + 1) / ONBOARDING_STAGES.length) * 100);
      const isOnProbation = record.status === 'OnProbation';
      const probationEnd = record.probationEndDate ? new Date(record.probationEndDate) : null;
      const daysToProbationEnd = probationEnd ? Math.ceil((probationEnd.getTime() - now) / (1000 * 60 * 60 * 24)) : null;

      const empName = `${record.legalEntity?.firstName ?? ''} ${record.legalEntity?.lastName ?? ''}`.trim();
      const employeeTraining = (trainingData || []).filter(t => t.employeeId === record.employeeNumber || t.employeeName === empName).slice(0, 5);
      const employeeDisciplinary = (disciplinaryData || []).filter(d => d.employeeId === record.employeeNumber || d.employeeName === empName).slice(0, 5);
      const employeeOHS = (ohsData || []).filter(o => o.employeeName === empName).slice(0, 5);
      const employeePIPs = (pipData || []).filter(p => p.employeeId === record.employeeNumber || p.employeeName === empName).slice(0, 5);
      const employeeOnboarding = (onboardingData || []).filter(o => o.employeeId === record.employeeNumber || o.employeeName === empName);
      const hasActiveOnboarding = employeeOnboarding.some(o => o.status !== 'Completed' && o.status !== 'Cancelled');

      const employeeFiles = (filesData || []).filter(f => {
        const empId = empName.toLowerCase().replace(/\s+/g, '');
        const fileEmpName = (f.employeeName || '').toLowerCase().replace(/\s+/g, '');
        return f.relatedEntity?.id === record.id || fileEmpName === empId || f.employeeId === record.id;
      });

      const handleAction = async (data: Record<string, unknown>) => {
        try {
          await update('employees', { id: record.id, data });
          notify('Stage updated successfully', { type: 'success' });
          refresh();
        } catch {
          notify('Error updating stage', { type: 'error' });
        }
      };

      return (
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <IconButton onClick={() => navigate('/employees')} size="small" sx={{ color: 'text.secondary' }}>
              <Close />
            </IconButton>
          </Box>
          <PageDescription entity="Employee" description="A person employed by the organisation. Employee records link to LegalEntity for personal details." icon={<Person />} />
          <Paper elevation={0} sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #1565c0, #1976d2)', color: 'primary.contrastText', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Avatar sx={{ width: 72, height: 72, bgcolor: 'secondary.main', fontSize: 28 }}>
                {record.legalEntity?.firstName?.[0]}{record.legalEntity?.lastName?.[0]}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5" fontWeight={700}>{record.legalEntity?.firstName} {record.legalEntity?.lastName}</Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>{record.seniority?.jobTitle} · {record.organisationalHierarchy?.department}</Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Chip label={record.status} size="small" color={STATUS_COLORS[record.status] as ChipColor} />
                  <Chip label={record.onboardingState?.currentStage?.replace(/([A-Z])/g, ' $1').trim()} size="small" color={STAGE_COLORS[record.onboardingState?.currentStage] as ChipColor} />
                </Box>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>Employee #</Typography>
                <Typography variant="h6" fontWeight={700}>{record.employeeNumber}</Typography>
              </Box>
            </Box>
          </Paper>

          {isOnProbation && daysToProbationEnd !== null && daysToProbationEnd > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }} icon={<Schedule />}>
              Probation ends in {daysToProbationEnd} days — schedule review meeting
            </Alert>
          )}

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Person color="primary" />
                  <Typography variant="subtitle1" fontWeight={700}>Personal Information</Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary">ID Number</Typography>
                    <Typography variant="body1">{record.legalEntity?.idNumber || '—'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary">Date of Birth</Typography>
                    <Typography variant="body1">{record.dateOfBirth || '—'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary">Gender</Typography>
                    <Typography variant="body1">{record.demographics?.gender || '—'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary">Race</Typography>
                    <Typography variant="body1">{record.demographics?.race || '—'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Email fontSize="small" color="action" />
                      <Typography variant="body2">{record.legalEntity?.email || '—'}</Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Phone fontSize="small" color="action" />
                      <Typography variant="body2">{record.legalEntity?.phone || '—'}</Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationOn fontSize="small" color="action" />
                      <Typography variant="body2">{record.contactDetails?.address || '—'}</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Work color="primary" />
                  <Typography variant="subtitle1" fontWeight={700}>Employment Details</Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary">Department</Typography>
                    <Typography variant="body1" fontWeight={500}>{record.organisationalHierarchy?.department || '—'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary">Position</Typography>
                    <Typography variant="body1">{record.seniority?.jobTitle || '—'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary">Contract Type</Typography>
                    <Typography variant="body1">{record.contractType?.replace(/([A-Z])/g, ' $1').trim() || '—'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary">Occupational Level</Typography>
                    <Typography variant="body1">{record.occupationalLevel?.replace(/([A-Z])/g, ' $1').trim() || '—'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarMonth fontSize="small" color="action" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">Start Date</Typography>
                        <Typography variant="body1">{record.employmentTerms?.startDate?.split('T')[0] || '—'}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Badge fontSize="small" color="action" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">Probation End</Typography>
                        <Typography variant="body1">{record.probationDetails?.endDate?.split('T')[0] || '—'}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <OnboardingProgress
              currentStage={record.currentStage ?? ''}
              contractType={record.contractType}
              status={record.status}
              onAdvanceStage={(stage) => handleAction({ currentStage: stage })}
            />

            <Grid size={{ xs: 12 }}>
              <Paper variant="outlined" sx={{ p: 3, my: 3, bgcolor: 'action.hover', borderTop: 4, borderTopColor: 'primary.main', borderLeft: 0, borderRight: 0, borderBottom: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <PlaylistAddCheck fontSize="small" color="primary" />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Process Actions</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {record.status === 'Pending' && (
                    <Button variant="contained" onClick={() => handleAction({ status: 'Active' })} startIcon={isUpdating ? <CircularProgress size={14} color="inherit" /> : <CheckCircle />} disabled={isUpdating}>
                      Activate Employee
                    </Button>
                  )}
                  {record.status === 'Active' && (
                    <>
                      <Button variant="contained" color="success" onClick={() => handleAction({ status: 'OnProbation', probationEndDate: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] })} startIcon={isUpdating ? <CircularProgress size={14} color="inherit" /> : <Schedule />} disabled={isUpdating}>
                        Begin Probation
                      </Button>
                      <Button variant="outlined" color="warning" onClick={() => handleAction({ status: 'Suspended' })} startIcon={isUpdating ? <CircularProgress size={14} color="inherit" /> : <Warning />} disabled={isUpdating}>
                        Suspend Employee
                      </Button>
                      <Button variant="outlined" color="error" onClick={() => navigate('/terminations/create?employeeId=' + record.employeeNumber + '&employeeName=' + encodeURIComponent(empName))} startIcon={<Person />}>
                        Terminate Employment
                      </Button>
                    </>
                  )}
                  {record.status === 'OnProbation' && (
                    <Button variant="contained" color="success" onClick={() => handleAction({ status: 'Active' })} startIcon={isUpdating ? <CircularProgress size={14} color="inherit" /> : <CheckCircle />} disabled={isUpdating}>
                      Confirm Probation
                    </Button>
                  )}
                  <Button variant="outlined" startIcon={<Warning />} onClick={() => navigate(`/wizard/contract-alert/${record.id}`)}>
                    Send Contract
                  </Button>
                </Box>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button component={Link} to="/training-records" variant="outlined" size="small" startIcon={<School />}>
                  All Training Records
                </Button>
                <Button component={Link} to="/disciplinary-cases" variant="outlined" size="small" startIcon={<Gavel />}>
                  All Disciplinary Cases
                </Button>
                <Button component={Link} to="/pips" variant="outlined" size="small" startIcon={<TrendingUp />}>
                  All PIPs
                </Button>
                <Button component={Link} to="/workplace-injuries" variant="outlined" size="small" startIcon={<HealthAndSafety />}>
                  All OHS Incidents
                </Button>
                {hasActiveOnboarding && (
                  <Button component={Link} to="/onboarding-processes" variant="outlined" size="small" startIcon={<CheckCircle />}>
                    Onboarding Process
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>

          <Paper elevation={0} sx={{ mt: 3 }}>
            <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tab label="Training History" />
              <Tab label="Disciplinary Cases" />
              <Tab label="Performance Reviews" />
              <Tab label="OHS Incidents" />
              <Tab label="Employee Files" />
            </Tabs>
            <Box sx={{ p: 3 }}>
              {activeTab === 0 && <EmployeeTraining records={employeeTraining} />}
              {activeTab === 1 && <EmployeeDisciplinary records={employeeDisciplinary} />}
              {activeTab === 2 && <EmployeePIPs records={employeePIPs} />}
              {activeTab === 3 && <EmployeeOHS records={employeeOHS} />}
              {activeTab === 4 && <EmployeeDocuments records={employeeFiles} />}
            </Box>
          </Paper>
        </Box>
      );
    }} />
  );
};
